/**
 * One-shot claim for automatically reloading the page when clerk-js never
 * arrives.
 *
 * Why this exists (incident 2026-07-29, recurred 2026-08-22 on the same
 * rescan-email conversion): clerk-js is CDN-hotloaded from the Clerk frontend
 * host and gates the entire dashboard. When it exceeds CLERK_LOAD_TIMEOUT_MS we
 * showed a failure card with a Retry button. Measured on production PostHog
 * 2026-08-05..2026-08-23: 28 of 103 people who opened the dashboard hit that
 * card, 27 of 31 of those sessions ended right there, and only 3 of 28 ever
 * pressed Retry. A reload provably recovers the load (verified in Chromium:
 * blocked script -> onerror, unblock + reload -> onload), so the product should
 * press Retry for them.
 *
 * EXACTLY ONCE is the whole safety property. A second automatic reload during a
 * real Clerk outage is an infinite reload loop, which is far worse for the user
 * than the dead end it replaces. So this is a check-and-CLAIM in a single call
 * rather than a separate read and write: a caller cannot accidentally reload
 * without having durably recorded that it did.
 *
 * It fails CLOSED. If storage is missing, unreadable, or unwritable we return
 * false and no reload happens. That is deliberate and is the direction that
 * matters: a missed auto-retry costs one user one manual click, while a reload
 * whose claim was never recorded loops forever.
 *
 * sessionStorage (not localStorage) is the right store: it survives the reload,
 * which is what makes "once" enforceable, and it is scoped to the tab and
 * cleared when the tab closes, so a user who hits a transient outage today still
 * gets an auto-retry in tomorrow's session.
 */

/** Storage key holding the claim. Survives the reload; dies with the tab. */
export const CLERK_AUTO_RETRY_KEY = 'oqa.clerkAutoRetried';

/**
 * Resolve sessionStorage without letting the lookup itself blow up the caller.
 *
 * Reading the `window.sessionStorage` PROPERTY throws SecurityError when the
 * origin is opaque or cookies are blocked (sandboxed iframe, some enterprise
 * policies, `about:` documents) -- before any getItem call. An unguarded
 * dereference in a React effect throws through React and breaks the auth screen,
 * which is worse than the dead end this module exists to fix. Same guard
 * TrialNudgeBanner.tsx:29 already applies for the same reason.
 */
function defaultSessionStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Claim the single automatic reload for this tab.
 *
 * Callers should omit the argument and let this resolve storage safely; the
 * parameter exists so tests can inject a store whose methods throw.
 *
 * @returns true only when the caller SHOULD reload: storage was usable, no
 *   prior claim existed, and the claim is now durably recorded. Any doubt
 *   returns false and the caller must show the manual failure card instead.
 */
export function claimClerkAutoRetry(
  storage: Storage | null | undefined = defaultSessionStorage(),
): boolean {
  return claimOnce(CLERK_AUTO_RETRY_KEY, storage);
}

/**
 * Check-and-claim under one key. Shared by both reload claims so neither can
 * drift from the fail-closed contract in the header.
 */
function claimOnce(key: string, storage: Storage | null | undefined): boolean {
  if (!storage) return false;

  // Reading can throw outright when the store is blocked or partitioned
  // (Safari private mode historically, enterprise policy, some extensions).
  // An unreadable store cannot prove this is the first attempt.
  let alreadyClaimed: string | null;
  try {
    alreadyClaimed = storage.getItem(key);
  } catch {
    return false;
  }
  if (alreadyClaimed) return false;

  // The write is what makes the claim survive the reload. If it throws (quota,
  // blocked store) we must NOT reload: the next document would read "not yet
  // claimed" and reload again, forever.
  try {
    storage.setItem(key, '1');
  } catch {
    return false;
  }

  return true;
}

/**
 * The WAKE retry: one more automatic reload, spent when the user comes back.
 *
 * Why a second claim exists (incident 2026-10-06, user-17e591): the first
 * automatic reload fires the instant the load fails, so it retries in the same
 * conditions that just failed. A trial owner whose browser reopens the
 * dashboard tab at the start of his day hit the failure on three of the four
 * days he returned; the reload failed too on two of them, and he never pressed
 * "Try again" on the card that followed. The watchdog's waited_ms read 33,392 /
 * 115,663 / 27,751 against a 10,000 ms timer, the mark of a throttled or frozen
 * page. Retrying again when the tab becomes visible or the network returns
 * (lib/useClerkWakeRetry.ts) retries when something has actually changed.
 *
 * The safety property is unchanged, only the bound moves: a tab now gets at
 * most TWO automatic reloads, one per key, and each claim is still recorded
 * before its reload. A separate key rather than a counter so the two can never
 * be confused for each other: the document that shows the failure card has
 * already spent the first.
 */
export const CLERK_WAKE_RETRY_KEY = 'oqa.clerkWakeRetried';

/**
 * Claim the single wake retry for this tab. Same contract as
 * claimClerkAutoRetry: true only when the caller SHOULD reload.
 */
export function claimClerkWakeRetry(
  storage: Storage | null | undefined = defaultSessionStorage(),
): boolean {
  return claimOnce(CLERK_WAKE_RETRY_KEY, storage);
}

/**
 * Has this tab already spent its wake retry? Read-only, and only ever used to
 * label telemetry (attempt 2 vs 3), so any doubt answers false instead of
 * throwing into the effect that reports the failure.
 */
export function clerkWakeRetrySpent(
  storage: Storage | null | undefined = defaultSessionStorage(),
): boolean {
  if (!storage) return false;
  try {
    return !!storage.getItem(CLERK_WAKE_RETRY_KEY);
  } catch {
    return false;
  }
}
